// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Bootstraps the app and owns the file's bytes. No parsing happens here — that
 * is the worker's job — and no DOM building happens here either.
 *
 * The one piece of real policy in this file is the interstitial before an
 * earlier version is downloaded. Recovering a previous save can undo a
 * redaction, and the person whose account number is under the black rectangle
 * is not the person pressing the button. So the first download in a session
 * asks; after that it does not, because a dialogue somebody has read once and
 * dismisses reflexively afterwards protects nobody and only trains them to
 * click through warnings.
 */

import './styles/main.css';

import { openModal, confirmModal } from './modal.ts';
import { bindLogToggle, clearLog, logEvent } from './eventlog.ts';
import { initGlossary } from './glossary.ts';
import { openRevision, renderPage, pageText } from './gate.ts';
import { buildApplicantHtml, buildCsv, buildReportHtml, formatBytes } from './report.ts';
import {
  ABOUT, BEFORE_YOU_ACT, HOW_IT_WORKS, PRIVACY, UNREDACTION_WARNING, WHAT_IT_CANNOT_TELL_YOU,
} from './copy.ts';
import {
  hideError, hideProgress, renderComparePane, renderCompareDiff, renderResults, renderShell,
  setProgress, showError, toast,
} from './ui.ts';
import type { Analysis, RevisionView, WorkerResponse } from './types.ts';

const MAX_BYTES = 512 * 1024 * 1024;

interface SampleEntry {
  file: string;
  label: string;
  blurb: string;
  revisions: number;
  bytes: number;
}

/* ── state ─────────────────────────────────────────────────────────── */

let currentBytes: Uint8Array | null = null;
let currentAnalysis: Analysis | null = null;

/**
 * The bytes of one saved version, as a standalone file.
 *
 * `.slice()`, never `.subarray()`. pdf.js's `getDocument({data})` TRANSFERS
 * `data.buffer` to its worker, so handing it a view over the original would
 * detach that buffer on this thread and silently zero every other revision. A
 * copy costs a few megabytes once; the alternative loses the file.
 *
 * The offset starts at the header rather than at zero, because every position
 * inside a PDF is measured from `%PDF-` and a file may legally have junk in
 * front of it.
 */
function sliceRevision(rev: RevisionView): Uint8Array<ArrayBuffer> | null {
  if (!currentBytes || !currentAnalysis) return null;
  const from = currentAnalysis.headerOffset;
  return currentBytes.slice(from, from + rev.endOffset) as Uint8Array<ArrayBuffer>;
}
let worker: Worker | null = null;
let hasAcknowledgedRecovery = false;
/** Guards against a stale compare render arriving after a newer one. */
let compareToken = 0;

const MODALS: Record<string, [string, string]> = {
  how: ['How it works', HOW_IT_WORKS],
  limits: ['What peelback can’t tell you', WHAT_IT_CANNOT_TELL_YOU],
  privacy: ['Privacy', PRIVACY],
  about: ['About peelback', ABOUT],
  act: ['Before you act on this', BEFORE_YOU_ACT],
};

/* ── boot ──────────────────────────────────────────────────────────── */

const root = document.getElementById('app');
if (root) {
  renderShell(root);
  initGlossary();
  wireModals();
  wireDropzone();
  wireLog();
  void loadSampleList();
  logEvent('info', 'Ready. Nothing has been uploaded and nothing will be.');
}

function wireModals() {
  document.addEventListener('click', (event) => {
    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-modal]');
    if (!trigger) return;
    const key = trigger.dataset.modal;
    const entry = key ? MODALS[key] : undefined;
    if (!entry) return;
    event.preventDefault();
    openModal(entry[0], entry[1]);
  });
}

function wireLog() {
  const toggle = document.getElementById('log-toggle');
  const count = document.getElementById('log-count');
  if (toggle && count) bindLogToggle(toggle, count);
}

/* ── input ─────────────────────────────────────────────────────────── */

function wireDropzone() {
  const zone = document.getElementById('dropzone');
  const input = document.getElementById('file-input') as HTMLInputElement | null;
  const retry = document.getElementById('error-retry');
  if (!zone || !input) return;

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      input.click();
    }
  });

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void ingest(file);
    // Cleared so choosing the same file twice in a row still fires.
    input.value = '';
  });

  for (const type of ['dragenter', 'dragover']) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.classList.add('is-dragging');
    });
  }
  for (const type of ['dragleave', 'drop']) {
    zone.addEventListener(type, (event) => {
      event.preventDefault();
      zone.classList.remove('is-dragging');
    });
  }
  zone.addEventListener('drop', (event) => {
    const file = (event as DragEvent).dataTransfer?.files?.[0];
    if (file) void ingest(file);
  });

  document.addEventListener('paste', (event) => {
    const items = (event as ClipboardEvent).clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (file) {
        event.preventDefault();
        void ingest(file);
        return;
      }
    }
  });

  retry?.addEventListener('click', () => {
    hideError();
    input.click();
  });
}

async function loadSampleList() {
  const host = document.getElementById('samples');
  if (!host) return;
  try {
    const response = await fetch('samples/index.json');
    if (!response.ok) throw new Error(String(response.status));
    const samples = (await response.json()) as SampleEntry[];
    for (const sample of samples) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'sample-pill';
      if (sample.label.startsWith('Trap')) pill.classList.add('is-trap');
      pill.title = sample.blurb;
      pill.textContent = sample.label;
      const tag = document.createElement('span');
      tag.className = 'pill-tag';
      tag.textContent = `${sample.revisions} save${sample.revisions === 1 ? '' : 's'}`;
      pill.append(tag);
      pill.addEventListener('click', (event) => {
        // The pill sits below the drop zone, but this is defensive: a sample
        // control inside a drop zone that does not stop propagation opens the
        // file picker as well as loading the sample.
        event.stopPropagation();
        void loadSample(sample);
      });
      host.append(pill);
    }
  } catch {
    // A missing sample list must not break the tool; the drop zone is the
    // primary path and is unaffected.
    logEvent('warn', 'The sample list could not be loaded. Drag a PDF in instead.');
  }
}

async function loadSample(sample: SampleEntry) {
  try {
    const response = await fetch(`samples/${sample.file}`);
    if (!response.ok) throw new Error(`the sample returned ${response.status}`);
    const blob = await response.blob();
    // Fed through exactly the same path as a real upload — never a shortcut.
    await ingest(new File([blob], sample.file, { type: 'application/pdf' }));
  } catch (err) {
    showError(`The sample could not be loaded: ${(err as Error).message}`);
  }
}

/* ── analysis ──────────────────────────────────────────────────────── */

async function ingest(file: File) {
  hideError();
  forgetFile(false);
  clearLog();

  logEvent('info', `Opening ${file.name} (${formatBytes(file.size)}). It stays on this device.`);

  if (file.size === 0) {
    showError('That file is empty — there are no bytes to read.');
    return;
  }
  if (file.size > MAX_BYTES) {
    showError(
      `That file is ${formatBytes(file.size)}, which is beyond the ${formatBytes(MAX_BYTES)} this tool will hold in memory. ` +
        'Nothing was uploaded; it simply cannot be read here.',
    );
    return;
  }

  setProgress('Reading the file', 0, 3);
  let buffer: ArrayBuffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (err) {
    hideProgress();
    showError(`The file could not be read from disk: ${(err as Error).message}`);
    return;
  }

  worker?.terminate();
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const message = event.data;
    if (message.kind === 'progress') {
      setProgress(message.phase, message.done, message.total);
      return;
    }
    if (message.kind === 'failed') {
      hideProgress();
      logEvent('bad', message.message);
      showError(message.message);
      return;
    }
    currentBytes = new Uint8Array(message.buffer);
    void present(message.analysis);
  };

  worker.onerror = (event) => {
    hideProgress();
    const message = event.message || 'the reader stopped unexpectedly';
    logEvent('bad', message);
    showError(`This file could not be read: ${message}`);
  };

  // Transferred rather than copied: a large PDF exists once in this tab, never
  // twice. It comes back with the result so earlier versions can be sliced out
  // of it here.
  worker.postMessage({ kind: 'analyse', fileName: file.name, buffer }, [buffer]);
}

async function present(analysis: Analysis) {
  currentAnalysis = analysis;

  logEvent('good', `Followed the index chain: ${analysis.saveCount} save${analysis.saveCount === 1 ? '' : 's'}.`);
  if (analysis.eofMarkerCount > analysis.saveCount) {
    logEvent(
      'info',
      `Counting %%EOF markers would have said ${analysis.eofMarkerCount}. The extra ${
        analysis.eofMarkerCount - analysis.saveCount
      } belong to web optimisation, an attachment, or page text.`,
    );
  }
  if (analysis.linearized) logEvent('info', 'This file is web-optimised, which is not a save.');
  if (analysis.attachments.length > 0) {
    logEvent('info', `${analysis.attachments.length} attachment(s): ${analysis.attachments.join(', ')}.`);
  }
  for (const w of analysis.warnings) logEvent('warn', w);

  /* The second opinion. peelback's own parser has already decided; pdf.js can
     only add a caveat, never promote a revision. Run before the results are
     drawn so a version that cannot be opened is never offered for download. */
  setProgress('Opening each version with a second PDF engine', 2, 3);
  for (const rev of analysis.revisions) {
    const slice = rev.structureOk ? sliceRevision(rev) : null;
    if (!slice) {
      logEvent('warn', `Save ${rev.index} did not re-read on its own: ${rev.structureError ?? 'unknown'}.`);
      continue;
    }
    const result = await openRevision(slice);
    rev.renderOk = result.ok;
    rev.renderError = result.error;
    rev.renderPageCount = result.pageCount;
    if (result.ok === true) {
      logEvent(
        'good',
        `Save ${rev.index}: re-read on its own and opened by a second engine — ${result.pageCount} page(s), ${formatBytes(
          rev.byteLength,
        )}.`,
      );
    } else if (result.ok === false) {
      logEvent(
        'bad',
        `Save ${rev.index} was rejected by a second engine: ${result.error ?? 'unknown'}. It is not offered for download.`,
      );
    } else {
      // Unknown, not failed. peelback's own strict parser has already accepted
      // this revision, and an engine that does not answer says nothing about
      // the document — so the file is still offered, with the gap stated.
      logEvent(
        'warn',
        `Save ${rev.index}: ${result.error ?? 'the second check could not be run'}. It is still offered, and this is said on the card.`,
      );
    }
  }

  hideProgress();

  renderResults(analysis, {
    onDownload: downloadRevision,
    onView: (rev) => focusCompareOn(rev),
    onReset: reset,
    onSaveReport: saveReport,
    onSaveCsv: saveCsv,
    onSaveApplicant: saveApplicantCopy,
    onForget: () => forgetFile(true),
    onCompareChange: () => void refreshCompare(),
  });

  if (analysis.saveCount > 1) void refreshCompare();
  document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ── the side-by-side reader ───────────────────────────────────────── */

function focusCompareOn(rev: RevisionView) {
  const left = document.getElementById('compare-left') as HTMLSelectElement | null;
  const right = document.getElementById('compare-right') as HTMLSelectElement | null;
  if (!left || !right) return;
  // Put the chosen version on the right, and whatever precedes it on the left.
  right.value = String(rev.index);
  const previous = currentAnalysis?.revisions
    .filter((r) => r.index < rev.index && r.structureOk && r.renderOk !== false)
    .pop();
  if (previous) left.value = String(previous.index);
  void refreshCompare();
  document.getElementById('compare-grid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

async function refreshCompare() {
  if (!currentAnalysis || !currentBytes) return;
  const leftSelect = document.getElementById('compare-left') as HTMLSelectElement | null;
  const rightSelect = document.getElementById('compare-right') as HTMLSelectElement | null;
  const pageInput = document.getElementById('compare-page') as HTMLInputElement | null;
  if (!leftSelect || !rightSelect || !pageInput) return;

  const token = ++compareToken;
  const page = Math.max(1, Number.parseInt(pageInput.value, 10) || 1);

  const find = (value: string) =>
    currentAnalysis?.revisions.find((r) => r.index === Number.parseInt(value, 10)) ?? null;
  const leftRev = find(leftSelect.value);
  const rightRev = find(rightSelect.value);

  const width = Math.min(520, Math.max(240, Math.floor(window.innerWidth / 2.4)));

  const draw = async (side: 'left' | 'right', rev: RevisionView | null) => {
    const slice = rev ? sliceRevision(rev) : null;
    if (!rev || !slice) {
      renderComparePane(side, '—', null, 'Choose a version.');
      return null;
    }
    const label = `Save ${rev.index}${rev.index === currentAnalysis?.saveCount ? ' (the file as it is now)' : ''}`;
    renderComparePane(side, label, null, 'Drawing the page…');
    const rendered = await renderPage(slice, page, width);
    if (token !== compareToken) return null;
    if (!rendered) {
      renderComparePane(side, label, null, `This version has no page ${page}, or it could not be drawn.`);
      return null;
    }
    renderComparePane(side, `${label} · page ${rendered.pageNumber}`, rendered.dataUrl, '');
    return pageText(slice, page);
  };

  const [leftText, rightText] = await Promise.all([draw('left', leftRev), draw('right', rightRev)]);
  if (token !== compareToken) return;
  renderCompareDiff(await leftText, await rightText);
}

/* ── downloads ─────────────────────────────────────────────────────── */

function save(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Revoked on the next turn: revoking synchronously races the download in
  // some browsers and produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function downloadRevision(rev: RevisionView) {
  if (!currentBytes || !currentAnalysis) return;
  const go = () => {
    const slice = sliceRevision(rev);
    if (!slice || !currentAnalysis) return;
    const base = currentAnalysis.fileName.replace(/\.pdf$/i, '');
    save(new Blob([slice], { type: 'application/pdf' }), `${base}.save-${rev.index}.pdf`);
    logEvent('good', `Saved save ${rev.index} — ${formatBytes(rev.byteLength)}, a complete PDF.`);
    toast(`Save ${rev.index} downloaded.`);
  };

  // Asked once per session. A dialogue that appears on every download is one
  // people learn to dismiss without reading, which protects nobody.
  if (hasAcknowledgedRecovery || rev.index === currentAnalysis.saveCount) {
    go();
    return;
  }
  confirmModal('Before you open an earlier version', UNREDACTION_WARNING, 'I understand — recover it', () => {
    hasAcknowledgedRecovery = true;
    go();
  });
}

function timestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function saveReport() {
  if (!currentAnalysis) return;
  const base = currentAnalysis.fileName.replace(/\.pdf$/i, '');
  save(
    new Blob([buildReportHtml(currentAnalysis, timestamp())], { type: 'text/html;charset=utf-8' }),
    `${base}.peelback-report.html`,
  );
  logEvent('good', 'Report saved. Every caveat shown on screen is written into the file itself.');
  toast('Report downloaded.');
}

function saveCsv() {
  if (!currentAnalysis) return;
  const base = currentAnalysis.fileName.replace(/\.pdf$/i, '');
  save(new Blob([buildCsv(currentAnalysis)], { type: 'text/csv;charset=utf-8' }), `${base}.changes.csv`);
  logEvent('good', 'changes.csv saved.');
  toast('CSV downloaded.');
}

function saveApplicantCopy() {
  if (!currentAnalysis) return;
  const base = currentAnalysis.fileName.replace(/\.pdf$/i, '');
  save(
    new Blob([buildApplicantHtml(currentAnalysis, timestamp())], { type: 'text/html;charset=utf-8' }),
    `${base}.for-the-sender.html`,
  );
  logEvent('good', 'A copy written for the person who sent the document was saved.');
  toast('Copy for the sender downloaded.');
}

/* ── clearing up ───────────────────────────────────────────────────── */

function forgetFile(announce: boolean) {
  currentBytes = null;
  currentAnalysis = null;
  compareToken += 1;
  worker?.terminate();
  worker = null;
  const results = document.getElementById('results');
  if (results) {
    results.replaceChildren();
    results.hidden = true;
  }
  if (announce) {
    logEvent('good', 'Cleared. The file and every recovered version are gone from this tab’s memory.');
    toast('Cleared from memory.');
    hideProgress();
    document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function reset() {
  forgetFile(false);
  clearLog();
  hideError();
  hideProgress();
  logEvent('info', 'Ready for another file.');
  document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
